import _ from 'lodash';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { DatePickerNew, Input, Select } from 'common-src/components/base';
import { isFeatureEnabled } from 'common-src/models/FeatureFlag';
import {
  createLabReading,
  getLabReadingUnit,
  getPatientLabResults,
  updateLabReading,
  validateLabReadingUniqueness,
  validatePatientLabReading,
} from 'common-src/models/PatientLabReading';
import { singleModelSelector } from 'common-src/utils/selectorUtils';

import useCustomSelector from 'src/hooks/useCustomSelector';
import usePopup from 'src/hooks/usePopup';
import BasePopup from 'src/popups/BasePopup';

import { initialState, SourceOptions, TypeOptions } from './constants';
import { extractLabResult, getAdditionalErrors, getIsButtonEnabled } from './helpers';
import styles from './LabPopup.module.scss';

const LabPopup = ({ onClose, patientId, labReadingId }) => {
  const dispatch = useDispatch();
  const [labResult, setLabResult] = useState(initialState);
  const [hasChanges, setHasChanges] = useState(false);

  const labResults = useCustomSelector((state) => getPatientLabResults(state, patientId));
  const labResultToEdit = useCustomSelector((state) =>
    singleModelSelector(state, 'PatientLabReading', labReadingId),
  );
  const isHedisEnabled = useCustomSelector((state) => isFeatureEnabled(state, 'hedis'));

  const { errors, setErrors, renderButtons, hasAlert } = usePopup();

  // check for lab record uniqueness
  const isUniqueRecord = useMemo(() => {
    if (hasAlert || !hasChanges) return true;
    return validateLabReadingUniqueness(labResults, labResult);
  }, [labResults, labResult, hasAlert, hasChanges]);

  const uniqErrorMessage = isUniqueRecord ? '' : 'The lab result already exists for this date';

  const handleErrors = (field, value) => {
    setErrors((prev) => ({
      ...prev,
      ...validatePatientLabReading(field, value),
      ...getAdditionalErrors(field, value, labResult),
    }));
  };

  useEffect(() => {
    if (_.isEmpty(labResultToEdit)) return;
    setLabResult(extractLabResult(labResultToEdit));
  }, [labResultToEdit]);

  const onChangeHandler = (field, value) => {
    if (!hasChanges) setHasChanges(true);
    handleErrors(field, value);
    setLabResult((prev) => ({ ...prev, [field]: value }));
  };

  const onSubmitHandler = (loadingCallback, successCallback, errorCallback, shouldClose) => {
    if (!isUniqueRecord) return;

    loadingCallback();

    const isEdit = !_.isEmpty(labResultToEdit);
    const action = isEdit ? updateLabReading : createLabReading;
    const successMessage = isEdit ? 'Lab Result edited!' : 'Lab Result added!';

    const payload = {
      ...labResult,
      patientId,
      readingDate: labResult.readingDate,
      readingValue: labResult.readingValue,
    };

    if (isEdit) {
      payload.id = labResultToEdit.id;
    }

    dispatch(
      action(payload, {
        successBlock: () => {
          successCallback(successMessage);

          if (!shouldClose) {
            setLabResult(initialState);
            return;
          }

          onClose(true);
        },
        errorBlock: (err) => errorCallback(err),
      }),
    );
  };

  const renderValueInput = () => {
    const type = TypeOptions.find((op) => op.value === labResult.typeId) || null;
    if (!type) return null;

    if (type.options) {
      return (
        <Select
          id="value-options-select"
          label={getLabReadingUnit(labResult.typeId) || 'Value'}
          placeholder={`Enter ${getLabReadingUnit(labResult.typeId) || 'value'}`}
          required
          options={type.options}
          onChange={(op) => onChangeHandler('readingValue', op.value)}
          value={type.options.find((op) => op.value === labResult.readingValue)}
          errorText={errors.readingValue || uniqErrorMessage}
          size="small"
        />
      );
    }

    return (
      <Input
        id="value-input"
        label={getLabReadingUnit(labResult.typeId) || 'Value'}
        placeholder={`Enter ${getLabReadingUnit(labResult.typeId) || 'value'}`}
        required
        value={labResult.readingValue}
        onTextChange={(value) => onChangeHandler('readingValue', value.replace(',', '.'))}
        onBlur={() => {
          if (errors.readingValue || !labResult.readingValue) return;
          setLabResult((prev) => ({ ...prev, readingValue: Number(labResult.readingValue) }));
        }}
        errorText={errors.readingValue || uniqErrorMessage}
        size="small"
      />
    );
  };

  return (
    <BasePopup
      id="lab-result"
      open
      onClose={onClose}
      title={labReadingId ? 'Edit Result' : 'Add Result'}
    >
      <div className={styles.container}>
        <Select
          id="type-select"
          required
          label="Result Type"
          placeholder="Select"
          options={TypeOptions}
          onChange={(op) => onChangeHandler('typeId', op.value)}
          value={TypeOptions.find((op) => op.value === labResult.typeId) || null}
          size="small"
          disabled={!isHedisEnabled}
          isSearchable
        />
        <Select
          id="patientReported"
          label="Source"
          options={SourceOptions}
          onChange={(op) => onChangeHandler('patientReported', op.value)}
          value={SourceOptions.find((op) => op.value === labResult.patientReported)}
          required
          placeholder="Select source"
          errorText={errors.patientReported}
          size="small"
        />
        {renderValueInput()}
        <DatePickerNew
          id="date-input"
          placeholder="Select Date"
          header="Result Date"
          required
          maxDate={new Date()}
          errorText={errors.readingDate}
          value={labResult.readingDate}
          onDateSelected={(date) =>
            onChangeHandler('readingDate', date ? moment(date).format('YYYY-MM-DD') : null)
          }
          onBlur={(date) =>
            setErrors((prev) => ({ ...prev, ...validatePatientLabReading('readingDate', date) }))
          }
          size="small"
        />
        {renderButtons({
          containerClassName: styles.buttonsContainer,
          onClose,
          onSubmit: onSubmitHandler,
          isSubmitEnabled: getIsButtonEnabled(labResult, labResultToEdit, errors) && isUniqueRecord,
          submitButtonText: 'Save and close',
          renderSaveButton: !labReadingId,
        })}
      </div>
    </BasePopup>
  );
};

LabPopup.propTypes = {
  onClose: PropTypes.func,
  patientId: PropTypes.number,
  labReadingId: PropTypes.number,
};

export default LabPopup;
